import { forwardRef, type AnchorHTMLAttributes, type ReactNode } from 'react';
import { CaretRight } from '@phosphor-icons/react';
import { cx } from '../cx';
import { Icon } from '../Icon';

export type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  href: string;
  /** `inline` sits in running text (underlined); `standalone` is a "See all" style link with a caret. */
  variant?: 'inline' | 'standalone';
  /** Opens in a new tab. Say so in the link text (callers own the wording). */
  external?: boolean;
  children: ReactNode;
};

/** A text link in the accent colour. Use Button with href for anything that looks like a button. */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { variant = 'inline', external, className, children, ...rest },
  ref,
) {
  return (
    <a
      {...rest}
      ref={ref}
      target={external ? '_blank' : rest.target}
      rel={external ? 'noopener noreferrer' : rest.rel}
      className={cx(
        'rounded-jc-info text-jc-accent transition-colors duration-(--jc-duration-fast) hover:text-jc-ink',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-focus focus-visible:ring-offset-2 focus-visible:ring-offset-jc-page',
        variant === 'inline'
          ? 'underline decoration-1 underline-offset-2 hover:decoration-2'
          : 'inline-flex items-center gap-1 text-sm font-semibold no-underline hover:underline',
        className,
      )}
    >
      {children}
      {variant === 'standalone' ? <Icon icon={CaretRight} size="sm" /> : null}
    </a>
  );
});
